import { faCode } from "@fortawesome/duotone-regular-svg-icons/faCode";
import { faDiamondTurnRight } from "@fortawesome/duotone-regular-svg-icons/faDiamondTurnRight";
import { faGlobePointer } from "@fortawesome/duotone-regular-svg-icons/faGlobePointer";
import { faMapPin } from "@fortawesome/duotone-regular-svg-icons/faMapPin";
import { faSlash } from "@fortawesome/duotone-regular-svg-icons/faSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Anchor,
  Card,
  Code,
  Flex,
  Grid,
  Group,
  Image,
  MantineShadow,
  Popover,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MapGeoJSONFeature, useMap } from "react-map-gl/maplibre";
import { useNavigate } from "react-router-dom";
import { setHoveredCampground } from "../../store/slices/hoveredCampground";
import { useAppDispatch } from "../../store/store";
import { TAGS_TO_ICONS } from "../../utils/amenityTags";
import { isDevMode } from "../../utils/debug";
import { getCampgroundHeroImageUrl } from "../../utils/images";
import StarRating from "../reviews/StarRating";
import styles from "./CampgroundCard.module.scss";
import {
  getCampgroundId,
  getCampgroundLocationString,
  getCampgroundName,
  getCampgroundOperatorString,
  useCampgroundDirectionsUrl,
} from "./campgroundUtils";
import FavoriteCampgroundButton from "./FavoriteCampgroundButton";
import FeeInfo from "./FeeInfo";
import { WeatherAverages } from "./WeatherAverages";

export interface CampgroundCardProps {
  campground: MapGeoJSONFeature;
  style?: React.CSSProperties;
  shadow?: MantineShadow;
  disableHover?: boolean;
}

const CampgroundCard = ({
  campground,
  style,
  shadow,
  disableHover,
}: CampgroundCardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { main: map } = useMap();
  const directionsUrl = useCampgroundDirectionsUrl(campground);

  if (!campground.properties) {
    return;
  }

  const properties = campground.properties;
  const campgroundName = getCampgroundName(campground);
  const campgroundId = getCampgroundId(campground);
  const campgroundImage = getCampgroundHeroImageUrl(campground);

  // Refs and state for overflow detection
  const amenityContainerRef = useRef<HTMLDivElement>(null);
  const [visibleIconCount, setVisibleIconCount] = useState<number>(0);

  // Memoize the Card style to prevent re-creating object on every render
  const cardStyle = useMemo(
    () => ({
      borderWidth: 2,
      cursor: "pointer",
      ...style,
    }),
    [style]
  );

  // Memoize event handlers to prevent child re-renders
  const handleCardClick = useCallback(() => {
    navigate(`/campground/${campgroundId}`, { state: { campground } });
  }, [navigate, campgroundId, campground]);

  const handleMouseEnter = useCallback(() => {
    if (disableHover) return;
    dispatch(setHoveredCampground(campground));
  }, [disableHover, dispatch, campground]);

  const handleMouseLeave = useCallback(() => {
    if (disableHover) return;
    dispatch(setHoveredCampground(undefined));
  }, [disableHover, dispatch]);

  const handleZoomToClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!map) return;
      map.flyTo({
        center: (campground.geometry as GeoJSON.Point).coordinates as [
          number,
          number
        ],
        zoom: 18,
        essential: true,
      });
    },
    [map, campground]
  );

  const handleStopPropagation = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Generate icons for amenities
  const amenityIcons = useMemo(
    () =>
      Object.entries(TAGS_TO_ICONS)
        .map(([tagName, iconDef]) => {
          if (properties[tagName] !== undefined) {
            const isAvailable =
              properties[tagName] != "no" &&
              properties[tagName] != "unavailable";
            return (
              <Tooltip
                key={tagName}
                label={(isAvailable ? "" : "no ") + iconDef.tooltip}
              >
                <div className={styles["tag-icon"]}>
                  {!isAvailable && (
                    <FontAwesomeIcon icon={faSlash} className={styles["slash-icon"]} />
                  )}
                  <FontAwesomeIcon icon={iconDef.icon} />
                </div>
              </Tooltip>
            );
          }
        })
        .filter((icon) => icon !== undefined),
    [properties]
  );

  // Memoize the calculation function to prevent recreation
  const calculateVisibleIcons = useCallback(() => {
    if (!amenityContainerRef.current) return;

    const containerWidth = amenityContainerRef.current.offsetWidth;

    // If container has no width yet, wait for next render
    if (containerWidth === 0) {
      return;
    }

    const iconWidth = 24; // from SCSS .tag-icon width
    const gapSize = 6.4; // 0.4rem ≈ 6.4px (assuming 16px base font-size)
    const overflowIndicatorWidth = 32; // +N indicator width (slightly wider than icon)

    // Calculate how many icons fit
    let maxIcons = Math.floor(
      (containerWidth + gapSize) / (iconWidth + gapSize)
    );

    // If we would need to show the overflow indicator, reserve space for it
    if (maxIcons < amenityIcons.length) {
      // Recalculate with space reserved for the +N indicator
      const availableWidthForIcons =
        containerWidth - overflowIndicatorWidth - gapSize;
      maxIcons = Math.floor(
        (availableWidthForIcons + gapSize) / (iconWidth + gapSize)
      );
      maxIcons = Math.max(0, maxIcons); // Ensure we don't go negative
    }

    setVisibleIconCount(maxIcons);
  }, [amenityIcons.length]);

  // Calculate how many icons can fit in the available space
  useLayoutEffect(() => {
    // Initial calculation
    const timer = setTimeout(calculateVisibleIcons, 0);

    // Create ResizeObserver only once
    const resizeObserver = new ResizeObserver(calculateVisibleIcons);
    if (amenityContainerRef.current) {
      resizeObserver.observe(amenityContainerRef.current);
    }

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [calculateVisibleIcons]);

  const websiteUrl = properties["website"];
  return (
    <>
      <Card
        shadow={shadow}
        padding="sm"
        radius="md"
        withBorder
        style={cardStyle}
        onClick={handleCardClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
          {campgroundImage && (
            <Card.Section mb={8}>
              <Image src={campgroundImage} h={160} />
            </Card.Section>
          )}
          <Grid>
            <Grid.Col ref={amenityContainerRef} span={8} display="flex">
              <Stack gap="0" flex="1" justify="space-between">
                <Group
                  gap="0.3rem"
                  wrap="nowrap"
                  align="flex-start"
                  className={styles["user-select-none"]}
                >
                  <Text fw={300} className={styles["campground-name"]}>
                    {campgroundName}
                  </Text>
                </Group>
                {/* Display rating if available */}
                {properties.average_rating && properties.review_count > 0 && (
                  <StarRating
                    value={properties.average_rating}
                    count={properties.review_count}
                    size="xs"
                  />
                )}
                <Text c="dimmed" size="sm">
                  {getCampgroundLocationString(campground)}
                </Text>
                {getCampgroundOperatorString(campground) && (
                  <Text c="dimmed" size="xs">
                    {getCampgroundOperatorString(campground)}
                  </Text>
                )}
                {(isDevMode() || (amenityIcons && amenityIcons.length > 0)) && (
                  <Group gap="xs" pt="0.3rem">
                    <Flex
                      direction="row"
                      gap="0.4rem"
                      wrap="nowrap"
                      className={styles["amenities-container"]}
                    >
                      {(() => {
                        // Split icons into visible and overflow
                        const count =
                          visibleIconCount > 0
                            ? visibleIconCount
                            : amenityIcons.length;
                        const visibleIcons = amenityIcons.slice(0, count);
                        const overflowIcons = amenityIcons.slice(count);

                        return (
                          <>
                            {visibleIcons}
                            {overflowIcons.length > 0 && (
                              <Tooltip
                                label={
                                  <Stack gap="0.4rem">{overflowIcons}</Stack>
                                }
                                withArrow
                                position="right"
                              >
                                <div className={`${styles["tag-icon"]} ${styles["overflow-indicator"]}`}>
                                  +{overflowIcons.length}
                                </div>
                              </Tooltip>
                            )}
                            {/* Display a debug icon in dev mode */}
                            {isDevMode() && (
                              <Popover
                                width={600}
                                position="right"
                                withArrow
                                shadow="md"
                              >
                                <Popover.Target>
                                  <div className={`${styles["tag-icon"]} ${styles["debug-icon"]}`}>
                                    <FontAwesomeIcon icon={faCode} size="xs" />
                                  </div>
                                </Popover.Target>
                                <Popover.Dropdown>
                                  <Code block>
                                    {JSON.stringify(
                                      campground.properties,
                                      null,
                                      2
                                    )}
                                  </Code>
                                </Popover.Dropdown>
                              </Popover>
                            )}
                          </>
                        );
                      })()}
                    </Flex>
                  </Group>
                )}
              </Stack>
            </Grid.Col>
            <Grid.Col span={4}>
              <Stack gap="0" align="flex-end">
                <Group gap="0.3rem" justify="flex-end">
                  <Tooltip label="zoom to">
                    <Anchor
                      autoFocus={false}
                      left={0}
                      onClick={handleZoomToClick}
                    >
                      <FontAwesomeIcon icon={faMapPin} />
                    </Anchor>
                  </Tooltip>
                  {directionsUrl && (
                    <Tooltip label="get directions">
                      <Anchor
                        left={0}
                        href={directionsUrl}
                        target="_blank"
                        autoFocus={false}
                        onClick={handleStopPropagation}
                      >
                        <FontAwesomeIcon icon={faDiamondTurnRight} />
                      </Anchor>
                    </Tooltip>
                  )}
                  {websiteUrl && (
                    <Tooltip label="open website">
                      <Anchor
                        left={0}
                        href={websiteUrl}
                        target="_blank"
                        autoFocus={false}
                        onClick={handleStopPropagation}
                      >
                        <FontAwesomeIcon icon={faGlobePointer} />
                      </Anchor>
                    </Tooltip>
                  )}
                  <FavoriteCampgroundButton campgroundId={campgroundId} />
                </Group>
                <FeeInfo campground={campground} />
                <WeatherAverages campground={campground} />
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>
    </>
  );
};

// Memoize the entire component to prevent re-renders when props haven't changed
export default memo(CampgroundCard);
